import type { CataloguePriceResource } from '@repo/api-client';
import { z } from 'zod';

import { formatPercentUnits, percentUnitsFrom } from '../revenue-shares/rate';

/**
 * The GST an item's price carries (blossom-rest-api#524): an HSN or SAC code
 * and a rate, **given together or not at all**.
 *
 * ## The pair is a switch, not two optional fields
 *
 * Two optional fields can be half-filled, and the API refuses a half-given pair
 * as `VALIDATION_FAILED` after the fact. So both forms hold a switch — does
 * this price charge GST — and the two fields appear only under it. What reaches
 * the wire is decided by the switch alone, in `taxOnTheWire`: the pair, or two
 * nulls. Text left in a field the switch has since hidden is never sent, so
 * there is no way to express half a pair.
 *
 * ## The rate is moved as text
 *
 * `taxRateBasisPoints` is hundredths of a percent — 18% is **1800**, never 18
 * and never 0.18. A person types a percentage, and `0.29 * 100` is
 * `28.999999999999996`, so the digits are moved as text by the same rule
 * `revenue-shares/rate.ts` uses for its thousandths. That file's *core* is
 * reused; its schema is not, because it fits neither end of this range: a
 * partner's share is 0.001% to 100% at three places, and a GST rate is 0% to
 * 100% at two — and **0% is a real rate here**, a nil-rated item that still
 * prints on a tax invoice, which is not the same as an untaxed one.
 *
 * ## The price stays tax-inclusive
 *
 * Nothing here computes tax. The API derives it downward from the price, and
 * the forms say so beside the rate.
 */

/** The API's `HSN_CODE_PATTERN`: 4, 6 or 8 digits. */
const HSN_SHAPE = /^\d{4}(\d{2}){0,2}$/;

/** Up to three whole digits and up to two decimals. No sign, no `%`, no exponent. */
const RATE_SHAPE = /^\d{1,3}(\.\d{1,2})?$/;

/** The published ceiling, `MAX_TAX_RATE_BASIS_POINTS`: 100%. */
const MAX_BASIS_POINTS = 10_000;

export const HSN_REQUIRED = 'Enter the HSN or SAC code the invoice prints for this item.';
export const HSN_MALFORMED = 'An HSN or SAC code is 4, 6 or 8 digits — 9985, for example.';
export const TAX_RATE_REQUIRED = 'Enter the GST rate. A nil-rated item is 0.';
export const TAX_RATE_MALFORMED =
  'A GST rate is a percentage with up to two decimal places — 18 or 2.5. No % sign and no minus.';
export const TAX_RATE_OUT_OF_RANGE = 'A GST rate is at most 100%.';

/** A percentage as typed, in basis points. Assumes `RATE_SHAPE` has matched. */
export const basisPointsFrom = (percent: string): number => percentUnitsFrom(percent, 2);

/** Basis points as somebody reads them: 1800 is `18%`, 25 is `0.25%`, 0 is `0%`. */
export const formatTaxRate = (basisPoints: number): string => formatPercentUnits(basisPoints, 2);

/**
 * The three fields, to spread into a form's `z.object`. Plain strings: the
 * rule that reads them together is `withTaxRule`, because whether either is
 * required depends on the switch.
 */
export const taxFields = {
  chargesGst: z.boolean(),
  hsnCode: z.string(),
  taxRate: z.string(),
};

export interface TaxValues {
  readonly chargesGst: boolean;
  readonly hsnCode: string;
  readonly taxRate: string;
}

/**
 * Adds the pair's rule to a form schema: with GST charged, both halves are
 * required and each must have its shape; without it, neither is looked at.
 */
export function withTaxRule<T extends z.ZodType<TaxValues>>(schema: T) {
  return schema.superRefine((values, context) => {
    if (!values.chargesGst) return;

    const hsnCode = values.hsnCode.trim();
    const rate = values.taxRate.trim();

    if (hsnCode === '') {
      context.addIssue({ code: 'custom', path: ['hsnCode'], message: HSN_REQUIRED });
    } else if (!HSN_SHAPE.test(hsnCode)) {
      context.addIssue({ code: 'custom', path: ['hsnCode'], message: HSN_MALFORMED });
    }

    if (rate === '') {
      context.addIssue({ code: 'custom', path: ['taxRate'], message: TAX_RATE_REQUIRED });
    } else if (!RATE_SHAPE.test(rate)) {
      context.addIssue({ code: 'custom', path: ['taxRate'], message: TAX_RATE_MALFORMED });
    } else if (basisPointsFrom(rate) > MAX_BASIS_POINTS) {
      context.addIssue({ code: 'custom', path: ['taxRate'], message: TAX_RATE_OUT_OF_RANGE });
    }
  });
}

/** What the body carries: the pair, or two nulls. Never one of each. */
export function taxOnTheWire(
  values: TaxValues,
): Pick<CataloguePriceResource, 'hsnCode' | 'taxRateBasisPoints'> {
  return values.chargesGst
    ? { hsnCode: values.hsnCode.trim(), taxRateBasisPoints: basisPointsFrom(values.taxRate) }
    : { hsnCode: null, taxRateBasisPoints: null };
}

/**
 * The fields opened on a price's own tax, or on none.
 *
 * A reprice opens here so that moving only the selling price restates the tax
 * in force rather than clearing it: the form always sends the pair, and the
 * pair it opens with is the one that stands.
 */
export function taxValuesFrom(
  price: Pick<CataloguePriceResource, 'hsnCode' | 'taxRateBasisPoints'> | null,
): TaxValues {
  if (price === null || price.hsnCode === null || price.taxRateBasisPoints === null) {
    return { chargesGst: false, hsnCode: '', taxRate: '' };
  }

  return {
    chargesGst: true,
    hsnCode: price.hsnCode,
    taxRate: formatTaxRate(price.taxRateBasisPoints).slice(0, -1),
  };
}
